import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import './css/wizard.scss';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { withRouter } from 'react-router-dom';

const Wizard = ({
  children,
  parentState,
  config,
  saveErrorsInState,
  submitData,
  wantedHero,
  lastPageRedirect,
  lastPageAutomaticRedirectAfter,
  history,
  isLoading,
  goTo,
  setGoTo,
  translations,
}) => {
  // Hook for store curent page index in state
  const [activePageIndex, setActivePageIndex] = useState(0);
  const pages = React.Children.toArray(children);
  const currentPage = pages[activePageIndex];
  const pageErrors = [];

  useEffect(() => {
    const timeout = _.parseInt(lastPageAutomaticRedirectAfter);
    if (activePageIndex === _.size(pages) - 1) {
      if (!_.isNaN(timeout) && timeout > 0) {
        window.timeoutRef = setTimeout(() => {
          history.push({ pathname: lastPageRedirect });
        }, timeout * 1000);
      }
    } else if (activePageIndex === -1) {
      history.push({ pathname: lastPageRedirect });
    }
  }, [activePageIndex]);

  const validateInputRules = () => {
    const { validationRules } = currentPage.props;
    const { validationMessages } = currentPage.props;

    _.forOwn(validationRules, (value, key) => {
      const rules = _.split(value, '|');
      _.map(rules, rule => {
        const ruleWithParameters = _.split(rule, ':');
        switch (ruleWithParameters[0]) {
          case 'required':
            // eslint-disable-next-line no-use-before-define
            proxyValidate(checkRequired, key, currentPage.props[key], validationMessages, ruleWithParameters[0]);
            break;
          case 'phone_number':
            // eslint-disable-next-line no-use-before-define
            proxyValidate(checkValidPhoneNumber, key, currentPage.props[key], validationMessages, ruleWithParameters[0]);
            break;
          case 'exists':
            // eslint-disable-next-line no-use-before-define
            proxyValidate(
              // eslint-disable-next-line no-use-before-define
              shouldExists,
              key,
              _.get(currentPage.props, ruleWithParameters[1]),
              validationMessages,
              ruleWithParameters[0],
              ruleWithParameters[1]
            );
            break;
          case 'checked':
            // eslint-disable-next-line no-use-before-define
            proxyValidate(shouldBeChecked, key, currentPage.props[key], validationMessages, ruleWithParameters[0]);
            break;
          default:
            break;
        }
      });
    });
  };

  /**
   * Validate the field calling the coresponding method and add or remove validation message
   * @param {function} _method
   * @param  {...any} args
   */
  const proxyValidate = (_method, ...args) => {
    const [key, value, validationMessages, rule, ruleParam] = args;
    const message = _method(value, validationMessages, rule, ruleParam);
    if (message) {
      if (_.has(pageErrors, key))
        // In case of multiple messages for the same field concat the new error
        pageErrors[key] = _.concat(pageErrors[key], { rule, message });
      else {
        // First message for the given field
        pageErrors[key] = [{ rule, message }];
      }
    } else {
      _.unset(pageErrors[key], rule);
    }
  };

  /**
   * Check if a field is required
   * @param {string} value
   * @param {Array} validationMessages
   * @param {string} rule
   * @returns string | null
   */
  const checkRequired = (value, validationMessages, rule) => {
    if (_.isEmpty(value)) {
      return validationMessages[rule];
    }

    return null;
  };

  /**
   * Check if the field is a possible valid phone number
   * @param {string} value
   * @param {Array} validationMessages
   * @param {string} rule
   * @returns
   */
  const checkValidPhoneNumber = (value, validationMessages, rule) => {
    if (!_.isEmpty(value)) {
      return isPossiblePhoneNumber(value) ? null : validationMessages[rule];
    }

    return null;
  };

  /**
   * Check if a value from dropdown was selected
   * @param {string} value
   * @param {Array} validationMessages
   * @param {string} rule
   * @param {string} ruleParam
   * @returns string | null
   */
  const shouldExists = (value, validationMessages, rule, ruleParam = null) => {
    // The selected value is stored in state
    const selectedValue = _.get(parentState, ruleParam);
    // In curent value is different from the selected value return validation error
    if (!value || value !== selectedValue) {
      return validationMessages[rule];
    }

    return null;
  };

  /**
   * Check if a checkbox was checked
   * @param {string} value
   * @param {Array} validationMessages
   * @param {string} rule
   * @returns string | null
   */
  const shouldBeChecked = (value, validationMessages, rule, ruleParam = null) => {
    if (!value) {
      return validationMessages[rule];
    }

    return null;
  };

  /**
   * If there are no validation errors from the backend side go to next page
   */
  const realyGoToNextPage = () => {
    let nextPage = 0;
    setActivePageIndex(index => {
      nextPage = index === _.size(pages) - 1 ? 0 : index + 1;
      return nextPage;
    });
    if (nextPage === 0) {
      clearTimeout(window.timeoutRef);
      history.push({ pathname: lastPageRedirect });
    }
  };

  /**
   * Store the curent page index in state
   */
  const goToNextPage = () => {
    // Validate input date and generate error messages
    validateInputRules();
    saveErrorsInState({ errors: pageErrors });
    if (_.size(_.keys(pageErrors)) === 0) {
      const submitAction = _.get(config, `page${activePageIndex + 1}.btn_next.action`, '');
      const sender = _.get(config, 'sender');
      if (submitAction) {
        const payload = activePageIndex === 0 && sender === 'checkin' ? wantedHero : submitData;
        submitAction(payload)
          .then(() => {
            realyGoToNextPage();
          })
          .catch(errors => {
            saveErrorsInState({ errors });
          });
      } else {
        realyGoToNextPage();
      }
    }
    setGoTo(null);
  };

  /**
   * Store the curent page index in state
   */
  const goToPrevPage = () => {
    setActivePageIndex(index => index - 1);
    setGoTo(null);
  };

  useEffect(() => {
    if (goTo === 'next') goToNextPage();
    else if (goTo === 'previous') goToPrevPage();
  }, [goTo]);

  /**
   * Render the current page
   */
  return isLoading ? (
    <div className="wizard">
      <div className="Loader loaderHeight">
        <h1 className="title">{_.get(translations, 'pleaseWait')}</h1>
      </div>
    </div>
  ) : (
    <div className="wizard">
      <div className="wizard__content">{currentPage}</div>
    </div>
  );
};

Wizard.propTypes = {
  children: PropTypes.arrayOf(PropTypes.shape),
  parentState: PropTypes.shape(),
  config: PropTypes.shape(),
  saveErrorsInState: PropTypes.func.isRequired,
  submitData: PropTypes.shape().isRequired,
  wantedHero: PropTypes.shape(),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  lastPageRedirect: PropTypes.string,
  lastPageAutomaticRedirectAfter: PropTypes.string,
  translations: PropTypes.shape(),
  isLoading: PropTypes.bool,
  goTo: PropTypes.string,
  setGoTo: PropTypes.func.isRequired,
};
Wizard.defaultProps = {
  children: [],
  parentState: {},
  config: {},
  lastPageRedirect: '',
  lastPageAutomaticRedirectAfter: '',
  translations: {},
  isLoading: false,
  goTo: null,
  wantedHero: null,
};

export default withRouter(Wizard);
